import { INDUSTRIES } from '@/landing/content'
import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const sample = INDUSTRIES.find((i) => i.key === 'auto')!

const page: ContentPageData = {
  path: '/dlya/avtoservisu',
  parent: { name: 'Галузі', path: '/dlya' },
  crumb: 'Автосервіси',
  title: 'AI-адміністратор для автосервісу та СТО | Anytel',
  description:
    'AI-агент приймає дзвінки в автосервіс 24/7: розпитує марку й симптоми, записує на діагностику у вільне вікно і передає заявку майстру в Telegram чи CRM.',
  eyebrow: 'Для автосервісів і СТО',
  h1: 'AI-адміністратор для автосервісу',
  lead:
    'Майстер-приймальник біля підйомника, руки в мастилі, а дзвінок важливий. AI-агент бере трубку замість нього: розпитує про авто й проблему, записує на вільний час і лишає майстру готову заявку.',
  sections: [
    {
      h2: 'Чому автосервіс пропускає дзвінки',
      blocks: [
        'В автосервісі телефон зазвичай у того, хто зайнятий найбільше: у приймальника, який водночас оформлює авто, рахує запчастини і говорить з клієнтом у залі. Дзвінок посеред цього або пропускають, або приймають на ходу і забувають записати.',
        {
          list: [
            'Клієнт зі стуком у підвісці дзвонить у три сервіси і їде туди, де відповіли першими.',
            'Після 18:00 і в неділю телефон мовчить, а саме тоді власники авто планують ремонт.',
            'Розмова без марки, року і симптомів ще не заявка: майстру доводиться передзвонювати й розпитувати знову.',
          ],
        },
      ],
    },
    {
      h2: 'Що робить AI-адміністратор в автосервісі',
      blocks: [
        { h3: 'Збирає все, що потрібно майстру' },
        'Марка, модель, рік, пробіг, що саме турбує і коли з’явилось. Агент ставить ці питання природно, по одному, і не завершує розмову, поки заявка не повна. Список питань ви задаєте самі.',
        { h3: 'Записує у вільне вікно' },
        'Пости, діагностика, шиномонтаж: для кожного свій календар з графіком. Агент пропонує найближчий вільний час і записує. Якщо ви не хочете, щоб він записував сам, агент приймає заявку, а час підтверджує приймальник.',
        { h3: 'Відповідає про ціни й терміни' },
        'Вартість діагностики, заміни мастила, шиномонтажу, графік роботи і як доїхати. Агент відповідає за вашою базою знань, а там, де ціна залежить від стану авто, чесно каже, що точну суму назве майстер після огляду.',
        { h3: 'Передає складне людині' },
        'Суперечка щодо виконаних робіт, гарантійний випадок, незвичне питання: такі дзвінки агент переводить на співробітника або лишає заявку на передзвін у робочий час.',
      ],
    },
  ],
  dialog: {
    h2: 'Приклад розмови',
    context: 'Автосервіс, вівторок, 19:40',
    dialog: sample.dialog,
    result: sample.result,
  },
  after: [
    {
      h2: 'Як це виглядає для вашої команди',
      blocks: [
        'Після дзвінка заявка з’являється в кабінеті і приходить у Telegram: хто дзвонив, яке авто, що турбує, на коли записаний. Майстер бачить це ще до того, як клієнт заїхав у бокс. Якщо ви ведете клієнтів у CRM, заявку туди передає [вебхук](/funktsii/integratsiya-z-crm).',
        'Кожна розмова записана: якщо клієнт потім каже «я ж говорив, що стукає зліва», запис можна прослухати і прочитати текстом.',
      ],
    },
    {
      h2: 'Як підключити',
      blocks: [
        {
          steps: [
            { title: 'Тестовий агент', text: 'Зареєструйтесь, оберіть шаблон і поговоріть з агентом з браузера ще до підключення номера.' },
            { title: 'Послуги, ціни і графік', text: 'Внесіть основні роботи з цінами, графік роботи і календарі постів. Перший сценарій можемо налаштувати разом.' },
            { title: 'Переадресація', text: 'Увімкніть переадресацію з номера сервісу: на весь час, лише коли ніхто не бере трубку або тільки в неробочі години.' },
          ],
        },
        'Найпростіший старт: віддати агенту вечори й вихідні. Так ви побачите, скільки заявок раніше губилось, нічого не змінюючи в робочому дні. Як це налаштувати, описано на сторінці [«Робота 24/7»](/funktsii/robota-24-7).',
      ],
    },
  ],
  faq: [
    {
      q: 'Агент розбереться в марках і моделях?',
      a: 'Так, назви марок і моделей сучасні мовні моделі розпізнають добре, зокрема коли клієнт вимовляє їх по-побутовому. Якщо щось прозвучало нечітко, агент перепитає. Перевірити легко тестовим дзвінком з браузера.',
    },
    {
      q: 'Чи може агент назвати точну вартість ремонту?',
      a: 'Лише ту, що є у вашій базі знань: фіксовані роботи на кшталт діагностики чи заміни мастила. Якщо ціна залежить від стану авто, агент записує на огляд і пояснює, що суму назве майстер.',
    },
    {
      q: 'У нас кілька постів і різні роботи. Як агент знає, куди записати?',
      a: 'Для кожного поста чи виду робіт створюється свій календар з графіком і тривалістю запису. Агент обирає календар за тим, що потрібно клієнту.',
    },
    {
      q: 'Що, якщо клієнт хоче поговорити з майстром?',
      a: 'Агент переведе дзвінок на співробітника. Якщо всі зайняті, прийме заявку, і система передзвонить клієнту в робочий час.',
    },
  ],
  related: ['/funktsii/ai-agent-dlya-dzvinkiv', '/funktsii/integratsiya-z-crm', '/funktsii/robota-24-7', '/blog/yak-ne-vtrachaty-dzvinky-pislya-18'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
